'use client';
import {type CSSProperties, type FC, memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {buildStorageBlurUrl, buildStorageTransformUrl, type OutputFormat, type ResizeMode} from './storageImageUrl';

interface SourceDescriptor {
  media: string;
  width: number;
  height?: number;
  resize?: ResizeMode;
  format?: OutputFormat;
  quality?: number;
  dprs?: number[];
}

interface StorageImageNewProps {
  src: string;
  alt: string;
  sources: SourceDescriptor[];
  className?: string;
  quality?: number;
  withBlur?: boolean;
  height?: number;
  resize?: ResizeMode;
  format?: OutputFormat;
  dprs?: number[];
  priority?: boolean;
  aspectRatio?: string;
  sizes?: string;
  onLoad?: () => void;
  onError?: () => void;
}

const buildDprSrcSet = (
  src: string,
  baseWidth: number,
  baseQuality: number,
  dprs: number[],
  perSource: {height?: number; resize?: ResizeMode; format?: OutputFormat; quality?: number},
  globalDefaults: {height?: number; resize?: ResizeMode; format?: OutputFormat},
): string => {
  const effectiveQuality = perSource.quality ?? baseQuality;
  const height = perSource.height ?? globalDefaults.height;
  const resize = perSource.resize ?? globalDefaults.resize;

  const parts = dprs.map(dpr => {
    const width = Math.max(1, Math.round(baseWidth * dpr));
    const h = typeof height === 'number' ? Math.max(1, Math.round(height * dpr)) : undefined;
    const url = buildStorageTransformUrl(src, {
      width,
      height: h,
      quality: effectiveQuality,
      resize,
    });
    return `${url} ${dpr}x`;
  });

  return parts.join(', ');
};

export const StorageImageNew: FC<StorageImageNewProps> = memo(
  ({
    src,
    alt,
    sources,
    className,
    quality = 75,
    withBlur = false,
    height,
    resize,
    format,
    dprs,
    priority = false,
    aspectRatio,
    sizes,
    onLoad,
    onError,
  }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);

    const globalDprs = useMemo(() => (dprs && dprs.length > 0 ? dprs : [1, 2]) as number[], [dprs]);
    const fallback = useMemo(() => sources[sources.length - 1], [sources]);
    const blur = useMemo(() => (withBlur ? buildStorageBlurUrl(src) : undefined), [withBlur, src]);

    const sourceElements = useMemo(
      () =>
        sources.map(({media, width, height: h, resize: r, format: f, quality: q, dprs: localDprs}) => {
          const srcSet = buildDprSrcSet(
            src,
            width,
            quality,
            localDprs && localDprs.length > 0 ? localDprs : globalDprs,
            {height: h, resize: r, format: f, quality: q},
            {height, resize, format},
          );
          return <source key={media} media={media} srcSet={srcSet} {...(sizes && {sizes})} />;
        }),
      [sources, src, quality, globalDprs, height, resize, format, sizes],
    );

    const fallbackSrc = useMemo(
      () =>
        buildStorageTransformUrl(src, {
          width: fallback.width,
          height: fallback.height ?? height,
          quality: fallback.quality ?? quality,
          resize: fallback.resize ?? resize,
        }),
      [src, fallback, height, quality, resize],
    );

    const handleImageLoad = useCallback(() => {
      setIsLoaded(true);
      onLoad?.();
    }, [onLoad]);

    const handleImageError = useCallback(() => {
      setIsLoaded(true);
      onError?.();
    }, [onError]);

    useEffect(() => {
      const img = imgRef.current;
      if (img && img.complete && img.naturalWidth > 0) {
        setIsLoaded(true);
      }
    }, [src]);

    const containerStyle = useMemo<CSSProperties>(() => {
      const style: CSSProperties = {};
      if (aspectRatio) style.aspectRatio = aspectRatio;
      return style;
    }, [aspectRatio]);

    if (withBlur && blur) {
      return (
        <div className={`relative overflow-hidden ${className || ''}`} style={containerStyle}>
          {!isLoaded && (
            <div
              className="absolute inset-0 z-[1]"
              style={{
                backgroundImage: `url(${blur})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'blur(10px)',
                transform: 'scale(1.1)',
              }}
            />
          )}
          <picture
            className={`block h-full w-full ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            style={{transition: 'opacity 0.3s ease-in-out'}}
          >
            {sourceElements}
            <img
              ref={imgRef}
              src={fallbackSrc}
              alt={alt}
              className="h-full w-full object-cover"
              loading={priority ? 'eager' : 'lazy'}
              decoding="async"
              fetchPriority={priority ? 'high' : undefined}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{display: 'block'}}
            />
          </picture>
        </div>
      );
    }

    return (
      <picture>
        {sourceElements}
        <img
          ref={imgRef}
          src={fallbackSrc}
          alt={alt}
          className={className}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={priority ? 'high' : undefined}
          style={containerStyle}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      </picture>
    );
  },
  (prevProps, nextProps) => {
    if (
      prevProps.src !== nextProps.src ||
      prevProps.alt !== nextProps.alt ||
      prevProps.className !== nextProps.className ||
      prevProps.quality !== nextProps.quality ||
      prevProps.withBlur !== nextProps.withBlur ||
      prevProps.priority !== nextProps.priority ||
      prevProps.aspectRatio !== nextProps.aspectRatio ||
      prevProps.sizes !== nextProps.sizes ||
      prevProps.height !== nextProps.height ||
      prevProps.resize !== nextProps.resize
    ) {
      return false;
    }
    if (prevProps.dprs?.length !== nextProps.dprs?.length) return false;
    if (prevProps.dprs && nextProps.dprs) {
      for (let i = 0; i < prevProps.dprs.length; i++) {
        if (prevProps.dprs[i] !== nextProps.dprs[i]) return false;
      }
    }
    if (prevProps.sources.length !== nextProps.sources.length) return false;
    for (let i = 0; i < prevProps.sources.length; i++) {
      const prev = prevProps.sources[i];
      const next = nextProps.sources[i];
      if (
        prev.media !== next.media ||
        prev.width !== next.width ||
        prev.height !== next.height ||
        prev.resize !== next.resize ||
        prev.quality !== next.quality ||
        prev.dprs?.length !== next.dprs?.length
      ) {
        return false;
      }
    }
    return true;
  },
);

StorageImageNew.displayName = 'StorageImageNew';

export type {StorageImageNewProps, SourceDescriptor};
